import { useQuery } from '@tanstack/react-query'
import { listContacts } from '@/api/people'
import { AddPersonInline } from '@/components/owed/AddPersonInline'
import { SplitSection, splitPayload, type SplitValue } from '@/components/owed/SplitSection'
import { Radio } from '@/components/ui/radio'
import { Select } from '@/components/ui/select'
import { splitError, toPaise } from '@/lib/splitMath'

export type ShareMode = 'JUST_US' | 'CHIP_IN' | 'PAID_BY'
export interface ShareValue {
  mode: ShareMode
  split: SplitValue
  paidByContactId: string
}

// eslint-disable-next-line react-refresh/only-export-components -- the modal and tests import these helpers alongside the component
export const EMPTY_SHARE: ShareValue = {
  mode: 'JUST_US',
  split: { rows: [], includeYou: true },
  paidByContactId: '',
}

// eslint-disable-next-line react-refresh/only-export-components -- see EMPTY_SHARE
export function shareError(v: ShareValue, amountRupees: number): string | null {
  if (v.mode === 'CHIP_IN')
    return splitError(
      toPaise(amountRupees || 0),
      v.split.rows.map((r) => r.amountPaise)
    )
  if (v.mode === 'PAID_BY' && !v.paidByContactId) return 'Choose who pays'
  return null
}

// eslint-disable-next-line react-refresh/only-export-components -- see EMPTY_SHARE
export function sharePayload(v: ShareValue): {
  splits?: { contactId: string; amount: number }[]
  paidByContactId?: string
} {
  if (v.mode === 'CHIP_IN') return { splits: splitPayload(v.split) }
  if (v.mode === 'PAID_BY') return { paidByContactId: v.paidByContactId }
  return {}
}

const MODES: [ShareMode, string][] = [
  ['JUST_US', 'Just us'],
  ['CHIP_IN', 'We pay — others chip in'],
  ['PAID_BY', 'Someone else pays — we chip in'],
]

export function RecurringShareSection({
  totalRupees,
  value,
  onChange,
}: {
  totalRupees: number
  value: ShareValue
  onChange: (v: ShareValue) => void
}) {
  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', { includeArchived: false }],
    queryFn: () => listContacts(false),
    enabled: value.mode === 'PAID_BY',
  })
  return (
    <fieldset className="space-y-2">
      <legend className="block text-sm font-medium text-[var(--fl-text-1)] mb-1">
        Split with people
      </legend>
      {MODES.map(([mode, label]) => (
        <label key={mode} className="flex items-center gap-2 text-sm text-[var(--fl-text-1)]">
          <Radio
            name="recurring-share"
            value={mode}
            checked={value.mode === mode}
            onChange={() => onChange({ ...value, mode })}
          />
          {label}
        </label>
      ))}
      {value.mode === 'CHIP_IN' && (
        <SplitSection
          totalRupees={totalRupees}
          value={value.split}
          onChange={(split) => onChange({ ...value, split })}
          excludeYouLabel="We're not part of this (collecting for them)"
          defaultOpen
        />
      )}
      {value.mode === 'PAID_BY' && (
        <div>
          <Select
            aria-label="Who pays"
            value={value.paidByContactId}
            onChange={(v) => onChange({ ...value, paidByContactId: v })}
          >
            <option value="">Select person</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <AddPersonInline onAdded={(c) => onChange({ ...value, paidByContactId: c.id })} />
          <p className="text-xs text-[var(--fl-text-2)] mt-1">
            Enter just your share as the amount. Mark as Paid adds it to what you owe them.
          </p>
        </div>
      )}
    </fieldset>
  )
}
